import type { Bucket, Currency, Deltas, TimeWindow } from '../../shared/types';
import { fmtSignedPct, fmtPct } from '../../shared/format';
import { useMask } from '../lib/mask';
import './../styles/components/bucket.css';

type Props = {
  bucket: Bucket;
  ccy: Currency;
  win: TimeWindow;
  active?: boolean;
  dimmed?: boolean;
  onClick?: () => void;
};

const WIN_TO_DELTA: Record<TimeWindow, keyof Deltas> = {
  '1D': 'd1',
  '7D': 'd7',
  '30D': 'd30',
  '90D': 'd90',
  '1Y': 'y1',
  '2Y': 'y2',
};

const DELTA_LABEL: Record<keyof Deltas, string> = {
  d1: '1D',
  d7: '7D',
  d30: '30D',
  d90: '90D',
  ytd: 'YTD',
  y1: '1Y',
  y2: '2Y',
};

const BUCKET_BAR: Record<string, string> = {
  crypto_long_term: 'var(--bucket-crypto)',
  etfs: 'var(--bucket-etfs)',
  reserves: 'var(--bucket-reserves)',
  finax: 'var(--bucket-finax)',
  stocks: 'var(--bucket-stocks)',
  saaspocalypse: 'var(--bucket-saas)',
  real_estate: 'var(--bucket-real-estate)',
  alternatives: 'var(--bucket-alternatives)',
  private_equity: 'var(--bucket-pe)',
};

export function BucketCard({ bucket: b, ccy, win, active, dimmed, onClick }: Props) {
  const { amount } = useMask();
  const value = ccy === 'USD' ? b.value_usd : b.value_eur;
  const bar = BUCKET_BAR[b.id] ?? b.color_accent;

  const midKey = win === '1D' || win === '1Y' ? 'd30' : WIN_TO_DELTA[win];
  const slots: (keyof Deltas)[] = ['d1', midKey, 'y1'];

  return (
    <article
      class={`bcard${active ? ' bcard-active' : ''}${dimmed ? ' bcard-dimmed' : ''}`}
      style={`--bucket-bar: ${bar}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    >
      <div class="bcard-head">
        <div class="bcard-title">{b.name}</div>
        <div class="bcard-share mono">{fmtPct(b.share)}</div>
      </div>
      <div class="bcard-value mono">{amount(value, ccy)}</div>
      <div class="bcard-deltas">
        {slots.map((k) => {
          const d = b.deltas[k];
          const cls = d.pct === 0 ? 'flat' : d.pct > 0 ? 'gain' : 'loss';
          return (
            <div class="bcard-delta" key={k}>
              <span class="bcard-delta-label mono">{DELTA_LABEL[k]}</span>
              <span class={`bcard-delta-value mono ${cls}`}>{fmtSignedPct(d.pct)}</span>
            </div>
          );
        })}
      </div>
      {b.pnl && (
        <div class="bcard-pnl">
          <span class="bcard-pnl-label mono">P&L</span>
          <span
            class={`bcard-pnl-abs mono ${b.pnl.pct >= 0 ? 'gain' : 'loss'}`}
          >
            {amount(ccy === 'USD' ? b.pnl.abs_usd : b.pnl.abs_eur, ccy, { signed: true })}
          </span>
          <span
            class={`bcard-pnl-pct mono ${b.pnl.pct >= 0 ? 'gain' : 'loss'}`}
          >
            {fmtSignedPct(b.pnl.pct)}
          </span>
        </div>
      )}
    </article>
  );
}
